import { useState } from "react";
import { useContextAwareGetProjectById } from "@hooks/index";
import { useAppSelector } from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import { NoteButton } from "@components/tabsButtons/TabsButtons";
import TranslatableText from "@components/translations/TranslatableText";
import useErrorMessage from "@utils/useErrorMessage";
import DownloadReportButtons from "./DownloadReportButton";
import ActivitiesCheckboxes from "./ActivitiesCheckboxes";
import DescriptionBlock from "./DescriptionBlock";
import BalanceByStatus from "@components/graphs/BalanceByStatus";
import CarbBalanceByYear from "@components/graphs/CarbBalanceByYear";
import CO2ByActivityBarChart from "@components/graphs/CO2ByActivityBarChart";
import BalanceCostRatio from "@components/graphs/BalanceCostRatio";
import TotalCO2Generated from "./TotalCO2Generated";
import InventoryResults from "./InventoryResults";
import MeanSpeciesAbundance from "./msa/MeanSpeciesAbundance";
import ProjectContentHeader from "../../ProjectContentHeader";
import type { ResultsTab } from "@app/features/builder/builderTypes";
import OpenMapButton from "../../OpenMapButton";

interface ProjectResultsProps {
    projectId?: string;
    isCompare?: boolean;
    resultsTab?: ResultsTab;
}

// B-intact specific results component (placeholder for now)
const BIntactProjectResults = ({ projectId, isCompare }: ProjectResultsProps) => {
    const {
        data: project,
        isError,
        error,
        isLoading
    } = useContextAwareGetProjectById(projectId || "", { refetchOnMountOrArgChange: true });
    const { errorMsg } = useErrorMessage({ error, isError });

    const { project: currentProject } = useAppSelector(selectCurrentProject);
    const isRenderingMainProject = projectId === currentProject?.id;

    if (errorMsg) {
        return (
            <div>
                <div className="module-group">
                    <span className="error fs-13 pb-2">
                        {errorMsg || `An error occurred while fetching for project ${projectId}`}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <section>
            <div className="module-group d-flex" style={isCompare ? { marginLeft: "90px" } : {}}>
                <ProjectContentHeader project={project} includeNotes={isRenderingMainProject} title="project.project_results" />
                <div className="open-map-buttons-row d-flex gap-8 py-2">
                    <OpenMapButton mode="baseline" />
                    <OpenMapButton mode="project" />
                </div>

                <div className="d-flex f-column py-2">
                    <div className="d-flex mb-1 pb-1 fs-13 lh-1 project-results-dc description-container">
                        <DescriptionBlock
                            country={project?.country?.name}
                            climate={project?.climate?.name}
                            moisture={project?.moisture?.name}
                            soilType={project?.soil_type?.name}
                            implDuration={project?.implementation_years}
                            capDuration={project?.capitalization_years}
                            isLoading={isLoading}
                            totalHectares={project?.total_hectares ? Math.round(project?.total_hectares) : undefined}
                            totalCatch={project?.total_catch?.w ? Math.round(project?.total_catch?.w) : undefined}
                            totalLivestock={project?.total_livestock?.w ? Math.round(project?.total_livestock?.w) : undefined}
                        />
                    </div>

                    {/* B-intact specific content will go here */}
                    <MeanSpeciesAbundance projectId={projectId} />
                </div>
            </div>
        </section>
    );
};

// Exact tool results component (existing functionality)
const ExactProjectResults = ({ projectId, isCompare }: ProjectResultsProps) => {
    const [selectedActivities, setSelectedActivities] = useState<number[]>();
    const handleActivitiesRefresh = (activities: number[]) => {
        setSelectedActivities(activities);
    };

    const {
        data: project,
        isError,
        error,
        isLoading
    } = useContextAwareGetProjectById(projectId || "", { refetchOnMountOrArgChange: true });
    const { errorMsg } = useErrorMessage({ error, isError });

    const { project: currentProject } = useAppSelector(selectCurrentProject);
    const isRenderingMainProject = projectId === currentProject?.id;

    if (errorMsg) {
        return (
            <section>
                <div className="module-group">
                    <span className="error fs-13 pb-2">
                        {errorMsg || `An error occurred while fetching for project ${projectId}`}
                    </span>
                </div>
            </section>
        );
    }

    return (
        <section>
            <div className="module-group d-flex" style={isCompare ? { marginLeft: "90px" } : {}}>
                <div className="pos-relative mb-2 pb-1">
                    <h2 className="fs-14 ff-medium-ext module-header w-fit">
                        <TranslatableText translationKey="project.project_results" />
                    </h2>
                    {isRenderingMainProject &&
                        <div className="pos-absolute d-flex align-items-center h-100" style={{ top: 0, left: "98%" }}>
                            <NoteButton />
                        </div>
                    }
                    <div className="module-number header-number ff-light-ext">{project?.id}</div>
                </div>

                <div className="d-flex f-column pb-2">
                    <div className="d-flex mb-1 pb-1 fs-13 lh-1 project-results-dc description-container">
                        <DescriptionBlock
                            country={project?.country?.name}
                            climate={project?.climate?.name}
                            moisture={project?.moisture?.name}
                            soilType={project?.soil_type?.name}
                            implDuration={project?.implementation_years}
                            capDuration={project?.capitalization_years}
                            isLoading={isLoading}
                            totalHectares={project?.total_hectares ? Math.round(project?.total_hectares) : undefined}
                            totalCatch={project?.total_catch?.w ? Math.round(project?.total_catch?.w) : undefined}
                            totalLivestock={project?.total_livestock?.w ? Math.round(project?.total_livestock?.w) : undefined}
                        />
                    </div>

                    <div className="d-flex gap-8">
                        <DownloadReportButtons selectedActivities={selectedActivities} projectId={project?.id ?? 0} />
                        <ActivitiesCheckboxes onRefresh={handleActivitiesRefresh} projectId={project?.id ?? 0} />
                    </div>

                    <TotalCO2Generated
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                    />

                    <CO2ByActivityBarChart
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                        height={100}
                        className="mb-3"
                    />

                    <BalanceByStatus
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                        height={350}
                        className="my-3 pe-2"
                    />

                    <CarbBalanceByYear
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                        height={350}
                        className="my-3 pe-2"
                    />

                    <BalanceCostRatio
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                    />

                    <InventoryResults
                        projectId={project?.id ?? 0}
                        selectedActivities={selectedActivities}
                    />
                </div>
            </div>
        </section>
    );
};

// Main ProjectResults component with conditional rendering
export const ProjectResults = ({ projectId, isCompare, resultsTab = "ghg" }: ProjectResultsProps) => {
    const activeTab = isCompare ? "ghg" : resultsTab;

    return activeTab === "ghg" ? (
        <ExactProjectResults projectId={projectId} isCompare={isCompare} />
    ) : (
        <BIntactProjectResults projectId={projectId} isCompare={isCompare} />
    );
};

export default ProjectResults; 